import React from 'react';
import cx from 'classnames';

import CloseS1 from '../Icons/CloseS1';
import IconButton from '../IconButton';
import InlineLink from '../InlineLink';
import Note from '../Note';
import { Props as NoteProps } from '../Note';

import styles from './Banner.module.scss';
import Text from '../Text';

export interface Props {
  children: React.ReactNode;
  className?: Parameters<typeof cx>[0];
  /** CTA text for InlineLink within Banner */
  ctaText?: string;
  /** Banner should show 'error' styling if true */
  error?: boolean;
  /** Any prop that Note accepts can be overridden with this object */
  noteProps?: NoteProps;
  /** URL to load when CTA InlineLink is clicked */
  href?: string;
  /** Icon to show toward the leading edge of the banner.
   * If this is supplied, the Note will not be displayed. Likewise,
   * the other way around. */
  icon?: React.ReactElement;
  /** Action to perform when CTA InlineLink is clicked */
  onClick?: () => void;
  /** Banner should show 'info' styling if true */
  success?: boolean;
  /** Target for the CTA InlineLink, e.g. `_blank` to open in a new tab/window. */
  target?: string;
  /** Text to display inside the Note component */
  title?: string | false;
  /** Banner should show 'warning' styling if true */
  warning?: boolean;
  /** Deprecated prop, do not use */
  buttonText?: string;
}

export const Banner: React.FC<Props> = ({
  children,
  className,
  ctaText,
  error,
  noteProps = {},
  href,
  icon,
  onClick,
  success,
  target,
  warning,
  title,
  // deprecated props
  buttonText,
}) => {
  // map deprecated props to new props
  if (buttonText != null) {
    ctaText = buttonText;
  }

  // Default title if none is provided
  if (title !== false && title == null) {
    if (error) {
      title = 'error';
    } else if (warning) {
      title = 'warning';
    } else if (success) {
      title = 'success';
    } else if (icon) {
      title = false;
    } else {
      title = 'info';
    }
  }

  return (
    <div
      role='alert'
      className={cx(
        styles.banner,
        error && styles.error,
        warning && styles.warning,
        success && styles.success,
        className,
      )}
    >
      <div className={cx(styles.container, href && styles.href)}>
        <div className={styles.content}>
          {icon && !title && (
            <span className={cx(styles.iconWrapper)}>{icon}</span>
          )}
          {title && !icon && (
            <Note
              small
              warning={warning}
              error={error}
              success={success}
              attention={!warning && !error && !success}
              className={styles.note}
              hasIcon={true}
              {...noteProps}
            >
              {title}
            </Note>
          )}
          <Text as='span' className={styles.text}>
            {children}
          </Text>
        </div>
        {onClick != null || href != null ? (
          ctaText != null ? (
            <InlineLink
              centered
              className={cx(styles.cta)}
              textClassName={cx(styles.ctaText)}
              href={href}
              onClick={onClick}
              target={target}
            >
              {ctaText}
            </InlineLink>
          ) : (
            <div className={styles.dismiss}>
              <IconButton
                icon={<CloseS1 />}
                onClick={onClick}
                href={href}
                accessibilityLabel='Dismiss banner'
                iconClassName={styles.dismissIcon}
              />
            </div>
          )
        ) : null}
      </div>
    </div>
  );
};

Banner.displayName = 'Banner';
export default Banner;
